import * as z from "zod/v4";
import {
    buildComponentDefaultValues,
    buildRateDefaultValues,
} from "./BillingRateBuilderShared";
import {
    formatNonAsciiHrpIdentifierError,
    hasNonAsciiHrpIdentifier,
} from "../../utils/billingRateUtils.js";

// A select/option field holds either an option object ({ value, label }) or null.
// "non-empty object" means an option with an actual value has been chosen.
const requiredOption = (message) =>
    z.any().refine(
        (v) => v != null && typeof v === "object" && v.value != null && v.value !== "",
        { message },
    );

const requiredString = (label, max) =>
    z
        .string({ error: `${label} is required` })
        .trim()
        .min(1, `${label} is required`)
        .max(max, `${label} must be ${max} characters or fewer`);

const asciiRequiredString = (label, max) => {
    const base = requiredString(label, max);
    return base.superRefine((value, ctx) => {
        if (!hasNonAsciiHrpIdentifier(value)) return;
        ctx.addIssue({
            code: "custom",
            message: formatNonAsciiHrpIdentifierError(label, value),
        });
    });
};

// Amounts come from text inputs, so they are validated as strings that must be
// formatted as a number with exactly two decimal places (e.g. "12.00").
const requiredAmount = (label) =>
    z
        .string({ error: `${label} is required` })
        .trim()
        .min(1, `${label} is required`)
        .regex(/^-?\d+\.\d{2}$/, `${label} must be a number with two decimal places (e.g. 12.00)`);

const requiredDate = (label) =>
    z
        .date({ error: `${label} is required` })
        .refine((d) => d instanceof Date && !Number.isNaN(d.getTime()), {
            message: `${label} must be a valid date`,
        });

const isFilledOption = (v) =>
    v != null && typeof v === "object" && v.value != null && v.value !== "";

// ---------------------------------------------------------------------------
// Step Two – Billing Rate Component
// ---------------------------------------------------------------------------

export const specificRateSchema = z.object({
    coverageTierName: requiredOption("Coverage tier name is required"),
    amount: requiredAmount("Rate amount"),
});

export const componentSchema = z
    .object({
        componentName: asciiRequiredString("Component name", 50),
        description: requiredString("Description", 100),
        componentId: asciiRequiredString("Component ID", 50),
        rateSubCategory: requiredOption("Rate sub category is required"),
        rateType: requiredOption("Rate type is required"),
        defaultRateAmount: requiredAmount("Default rate amount"),
        tiered: z.boolean().nullish(),
        coverageTierSetName: z.any().nullish(),
        specificRates: z.array(z.any()).nullish(),
    })
    .superRefine((value, ctx) => {
        if (!value.tiered) return;

        if (!isFilledOption(value.coverageTierSetName)) {
            ctx.addIssue({
                code: "custom",
                path: ["coverageTierSetName"],
                message: "Coverage tier set name is required",
            });
        }

        const rates = Array.isArray(value.specificRates) ? value.specificRates : [];
        if (rates.length === 0) {
            ctx.addIssue({
                code: "custom",
                path: ["specificRates"],
                message: "At least one specific rate is required",
            });
        }

        rates.forEach((rate, index) => {
            const result = specificRateSchema.safeParse(rate);
            if (!result.success) {
                result.error.issues.forEach((issue) => {
                    ctx.addIssue({
                        code: "custom",
                        path: ["specificRates", index, ...issue.path],
                        message: issue.message,
                    });
                });
            }
        });
    });

// ---------------------------------------------------------------------------
// Step Three – Billing Rate
// ---------------------------------------------------------------------------

export const billingRateDefinitionSchema = z.object({
    componentReference: z.object({
        component: requiredOption("Component is required"),
        lockDate: requiredDate("Lock date"),
    }),
}).superRefine((value, ctx) => {
    const componentRef = value?.componentReference?.component;
    const componentValue = typeof componentRef === "string"
        ? componentRef
        : (componentRef?.value ?? componentRef?.label ?? "");
    if (hasNonAsciiHrpIdentifier(componentValue)) {
        ctx.addIssue({
            code: "custom",
            path: ["componentReference", "component"],
            message: formatNonAsciiHrpIdentifierError("Component reference", componentValue),
        });
    }
});

export const billingCategoryRateSchema = z.object({
    billingCategory: requiredOption("Billing category is required"),
    partialIntervalCalculationMethod: requiredOption(
        "Partial interval calculation method is required",
    ),
    billingRateDefinitions: z
        .array(billingRateDefinitionSchema)
        .min(1, "At least one billing rate is required"),
});

export const rateSchema = z.object({
    account: requiredOption("Account is required"),
    benefitPlan: requiredOption("Benefit plan is required"),
    billingCategoryRates: z
        .array(billingCategoryRateSchema)
        .min(1, "At least one billing category rate is required"),
});

// Validates only the rate header fields (account, benefit plan, and that at
// least one category exists). Used when RateEditBody is displayed — individual
// category validity is irrelevant at this level.
export const rateHeaderSchema = z.object({
    account: requiredOption("Account is required"),
    benefitPlan: requiredOption("Benefit plan is required"),
    billingCategoryRates: z.array(z.any()).min(1, "At least one billing category rate is required"),
});

// Returns a schema that validates only the billing category rate at `index`.
// Used when CategoryRateEditBody is displayed — only that single category
// needs to be valid.
export const buildRateCategorySliceSchema = (index) =>
    z.object({
        billingCategoryRates: z.array(z.any()).superRefine((rates, ctx) => {
            const result = billingCategoryRateSchema.safeParse(rates[index]);
            if (!result.success) {
                result.error.issues.forEach((issue) => {
                    ctx.addIssue({
                        code: "custom",
                        path: [index, ...issue.path],
                        message: issue.message,
                    });
                });
            }
        }),
    });

// ---------------------------------------------------------------------------
// Payload validation
//
// The schemas above validate the form-shaped values produced by the edit forms.
// To validate the HRP-shaped payloads with the exact same rules, we reuse the
// shared buildComponentDefaultValues / buildRateDefaultValues adapters (also
// used by the edit forms) to convert each payload into the form-values shape,
// then run it through the same schema.
// ---------------------------------------------------------------------------

// Translate a generic "is required" message into a more precise "doesn't exist"
// message when a sub category was present in the source data but couldn't be
// matched during lookup enrichment (shortName present, codeEntry absent).
const translateComponentMessage = (payload, key, message) => {
    const rsc =
        payload?.billingRateComponentPayload?.billingRateComponent?.definition
            ?.rateClassifications?.rateSubCategory;
    // The matched codeEntry lives on the wrapper's sidecar match metadata, not on
    // the payload's rateSubCategory.
    const codeEntry = payload?.subCategoryMatch?.codeEntry;
    const rscNotFound = Boolean(rsc?.shortName && !codeEntry);
    return rscNotFound && key === "rateSubCategory" && message === "Rate sub category is required"
        ? "Rate sub category doesn't exist in HRP"
        : message;
};

// Translate "Billing category is required" into "Billing category doesn't exist"
// when the category was present in the source data (codeEntry) but couldn't be
// matched during lookup enrichment (shortName absent).
const translateRateMessage = (payload, path, message) => {
    if (message !== "Billing category is required" || path.length < 3) return message;
    const billingCategoryRates =
        payload?.billingRatePayload?.billingRate?.billingCategoryRates ?? [];
    const cat = billingCategoryRates[path[1]];
    // shortName lives on the wrapper's billingCategoryShortNames dictionary, not
    // on the payload's billingCategory.
    const codeEntry = cat?.billingCategory?.codeEntry;
    return codeEntry && !payload?.billingCategoryShortNames?.[codeEntry]
        ? "Billing category doesn't exist in HRP"
        : message;
};

// Parse a payload against its schema and return a normalized list of issues,
// each with the field path, a dotted key, and a (possibly translated) message.
// This is the single source of truth that both the flat-message validators and
// the field-error maps are derived from.
const collectIssues = (schema, formValues, translate) => {
    const result = schema.safeParse(formValues);
    if (result.success) return [];
    return result.error.issues.map((issue) => {
        const key = issue.path.join(".");
        return { path: issue.path, key, message: translate(issue.path, key, issue.message) };
    });
};

const getComponentIssues = (payload) => {
    const schemaIssues = collectIssues(componentSchema, buildComponentDefaultValues(payload), (_path, key, message) =>
        translateComponentMessage(payload, key, message),
    );
    const payloadIssues = [];
    const component = payload?.billingRateComponentPayload?.billingRateComponent;
    const componentName = component?.componentName;
    const componentId = component?.definition?.rateClassifications?.componentId;
    if (hasNonAsciiHrpIdentifier(componentName)) {
        payloadIssues.push({
            path: ["componentName"],
            key: "componentName",
            message: formatNonAsciiHrpIdentifierError("Component name", componentName),
        });
    }
    if (hasNonAsciiHrpIdentifier(componentId)) {
        payloadIssues.push({
            path: ["componentId"],
            key: "componentId",
            message: formatNonAsciiHrpIdentifierError("Component ID", componentId),
        });
    }
    return [...schemaIssues, ...payloadIssues];
};

const getRatePayloadAsciiIssues = (payload) => {
    const issues = [];
    const billingRate = payload?.billingRatePayload?.billingRate;
    (billingRate?.billingCategoryRates ?? []).forEach((cat, catIndex) => {
        (cat?.billingRateDefinitions ?? []).forEach((def, defIndex) => {
            const component = def?.componentReference?.component;
            if (!hasNonAsciiHrpIdentifier(component)) return;
            issues.push({
                path: ["billingCategoryRates", catIndex, "billingRateDefinitions", defIndex, "componentReference", "component"],
                key: `billingCategoryRates.${catIndex}.billingRateDefinitions.${defIndex}.componentReference.component`,
                message: formatNonAsciiHrpIdentifierError("Component reference", component),
            });
        });
    });
    return issues;
};

const getRateIssues = (payload) => {
    const schemaIssues = collectIssues(rateSchema, buildRateDefaultValues(payload), (path, _key, message) =>
        translateRateMessage(payload, path, message),
    );
    return [...schemaIssues, ...getRatePayloadAsciiIssues(payload)];
};

const uniqueMessages = (issues) => {
    const seen = new Set();
    const messages = [];
    (issues ?? []).forEach(({ message }) => {
        if (message && !seen.has(message)) {
            seen.add(message);
            messages.push(message);
        }
    });
    return messages;
};

// Convert a list of issues into a { dottedPath: message } map (first wins).
const issuesToFieldErrors = (issues) => {
    const errors = {};
    issues.forEach(({ key, message }) => {
        if (!errors[key]) errors[key] = message;
    });
    return errors;
};

// Validate a payload, returning the validity flag, the de-duplicated flat list
// of messages (used by the summary alerts), and a field-keyed error map (used
// by the detail/view modals). All derived from the same parsed issues.
export const validateComponentPayload = (payload) => {
    const issues = getComponentIssues(payload);
    return {
        valid: issues.length === 0,
        errors: uniqueMessages(issues),
        fieldErrors: issuesToFieldErrors(issues),
    };
};

export const validateRatePayload = (payload) => {
    const issues = getRateIssues(payload);
    return {
        valid: issues.length === 0,
        errors: uniqueMessages(issues),
        fieldErrors: issuesToFieldErrors(issues),
    };
};

const componentLabel = (payload) => {
    const c = payload?.billingRateComponentPayload?.billingRateComponent;
    return c?.componentName || c?.definition?.rateClassifications?.componentId || "";
};

const rateLabel = (payload) => {
    return payload?.benefitPlan || payload?.plan?.name || "";
};

// Cross-payload uniqueness check. For each extractor, any value shared by two or
// more payloads flags every payload holding that value with the given message.
// Returns a Map<index, string[]> of extra error messages keyed by payload index.
const findDuplicateErrors = (payloads, extractors) => {
    const errorsByIndex = new Map();
    extractors.forEach(({ getValue, message }) => {
        const indexesByValue = new Map();
        (payloads ?? []).forEach((payload, index) => {
            const raw = getValue(payload);
            if (raw == null) return;
            const key = String(raw).trim();
            if (key === "") return; // empty/missing is handled by the "is required" rule
            if (!indexesByValue.has(key)) indexesByValue.set(key, []);
            indexesByValue.get(key).push(index);
        });
        indexesByValue.forEach((indexes) => {
            if (indexes.length < 2) return;
            indexes.forEach((index) => {
                if (!errorsByIndex.has(index)) errorsByIndex.set(index, []);
                errorsByIndex.get(index).push(message);
            });
        });
    });
    return errorsByIndex;
};

// `duplicateErrorsByIndex` is an optional Map<index, string[]> of cross-payload
// (uniqueness) errors that are merged with each payload's own schema errors.
// Set prefixRows (steps two/three) to prepend "Row <n>, <label>: " to each line.
const summarize = (payloads, validateOne, labelOf, duplicateErrorsByIndex, { prefixRows = false } = {}) => {
    const invalidIndexes = new Set();
    const bodyLines = [];
    (payloads ?? []).forEach((payload, index) => {
        const { errors } = validateOne(payload);
        const duplicateErrors = duplicateErrorsByIndex?.get(index) ?? [];
        const allErrors = [...errors, ...duplicateErrors];
        if (allErrors.length === 0) return;
        invalidIndexes.add(index);

        const label = labelOf(payload);
        const prefix = prefixRows ? `Row ${index + 1}, ${label}` : `${label}`;
        allErrors.forEach((message) => bodyLines.push(`${prefix}: ${message}`));
    });
    return {
        hasErrors: invalidIndexes.size > 0,
        invalidCount: invalidIndexes.size,
        invalidIndexes,
        bodyLines,
    };
};

const getComponentName = (payload) =>
    payload?.billingRateComponentPayload?.billingRateComponent?.componentName;

const getComponentId = (payload) =>
    payload?.billingRateComponentPayload?.billingRateComponent?.definition
        ?.rateClassifications?.componentId;

const getBenefitPlanId = (payload) =>
    payload?.benefitPlan ?? payload?.plan?.name;

export const summarizeComponentValidation = (payloads, { prefixRows = false } = {}) =>
    summarize(
        payloads,
        validateComponentPayload,
        componentLabel,
        findDuplicateErrors(payloads, [
            { getValue: getComponentName, message: "Component name must be unique" },
            { getValue: getComponentId, message: "Component ID must be unique" },
        ]),
        { prefixRows },
    );

export const summarizeRateValidation = (payloads, { prefixRows = false } = {}) =>
    summarize(
        payloads,
        validateRatePayload,
        rateLabel,
        findDuplicateErrors(payloads, [
            { getValue: getBenefitPlanId, message: "Benefit plan IDs must be unique" },
        ]),
        { prefixRows },
    );
